import clsx from "clsx";
import { BookA, CircleHelp, Compass, CornerDownRight, MessageSquarePlus, Search, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { EmptyState } from "../components/EmptyState";
import { Disclosure, DisclosureChevron } from "../components/panel";
import { motion, usePresence } from "../components/presence";
import { Badge, Button, IconButton, Kbd, SearchField, Segmented, SectionLabel } from "../components/ui";
import { useDismiss } from "../components/useDismiss";
import { t } from "../i18n/i18n";
import { designateSoon } from "../components/attention";
import { launcherSelector, setFeedbackMenu, visibleFeedbackPlace } from "../feedback/feedbackModel";
import { plural } from "../plural";
import { glossaryAnchor, glossaryEntries } from "./glossary";
import { GuideHelpCard } from "./guide";
import { type HelpTab, useHelp } from "./helpStore";
import { RotationFlowFigure } from "./RotationFlowFigure";
import {
  fold,
  helpTopics,
  type HelpRole,
  type HelpScreen,
  type HelpTopic,
  matches,
  ROLE_LABELS,
  SCREENS,
  screenHasTopics,
  screenTitle,
  topicById,
  topicsOf,
} from "./topics";

/** Centre d'aide (l'aide contextuelle de Generex, à la charte web) : volet à droite avec les fiches de l'écran
 *  affiché, toute l'aide (recherche, filtre Joueur / Direx) et le glossaire des sigles. Échap ou un clic dehors
 *  le ferme. Fiches, écrans et glossaire suivent la langue de l'interface (topics.en.ts, glossary.en.ts). Plan 70
 *  (R9), voile `bg-ink/20`. */
export function HelpCenter() {
  const open = useHelp((s) => s.open);
  const { mounted, closing } = usePresence(open);
  if (!mounted) return null;
  return createPortal(<Sheet closing={closing} />, document.body);
}

/** Couleur du public d'une fiche (docs/02 § 4.1) : Joueur bleu, Direx orange, Commun gris. */
const ROLE_TONES: Record<HelpRole, "friend" | "warn" | "neutral"> = {
  joueur: "friend",
  direx: "warn",
  commun: "neutral",
};

type RoleFilter = HelpRole | "tout";

function screenLabel(key: HelpScreen): string {
  return screenTitle(key) ?? t("Vue à venir");
}

function Sheet({ closing }: { closing: boolean }) {
  // Écran sans fiche dans le contexte affiché (RETEX face à l'ennemi générique, registre B39) : masqué, l'aide
  // s'ouvre sur « Toute l'aide ».
  const requested = useHelp((s) => s.screen);
  const screen = requested && screenHasTopics(requested) ? requested : null;
  const requestedTab = useHelp((s) => s.tab);
  const tab: HelpTab = requestedTab === "screen" && requested && !screen ? "all" : requestedTab;
  const topic = useHelp((s) => s.topic);
  const closeHelp = useHelp((s) => s.closeHelp);
  const openOnboarding = useHelp((s) => s.openOnboarding);
  const setTab = (next: HelpTab) => useHelp.setState({ tab: next, topic: null });
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<RoleFilter>("tout");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(topic ? [topic] : []));
  const panel = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const close = useCallback(() => closeHelp(), [closeHelp]);
  // « Une idée, une anomalie ? » : l'aide se referme, puis le bouton « Retours » visible pulse et ouvre son menu.
  const askFeedback = () => {
    const place = visibleFeedbackPlace();
    closeHelp();
    window.setTimeout(() => setFeedbackMenu(place), 180);
    designateSoon(launcherSelector(place), "ring", 400);
  };
  useDismiss(!closing, close, panel);

  // Fiche demandée (lien « Voir aussi », recherche ⌘K, ouverture sur une fiche ou un sigle) : dépliée et amenée à
  // l'écran.
  useEffect(() => {
    if (!topic) return;
    setExpanded((prev) => new Set(prev).add(topic));
    const frame = requestAnimationFrame(() =>
      document.getElementById(`aide-${topic}`)?.scrollIntoView({ block: tab === "glossary" ? "center" : "start", behavior: "smooth" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [topic, tab]);
  useEffect(() => {
    if (tab !== "screen") search.current?.focus();
    body.current?.scrollTo({ top: 0 });
  }, [tab]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const goTo = (id: string) => {
    const target = topicById(id);
    if (!target) return;
    // Même écran : dépliée sur place ; sinon dans « Toute l'aide », sans filtre qui la cacherait.
    if (tab === "screen" && target.screen === screen) useHelp.setState({ topic: id });
    else {
      setQuery("");
      setRole("tout");
      useHelp.setState({ tab: "all", topic: id });
    }
  };

  const screenTopics = screen ? topicsOf(screen) : [];
  const results = useMemo(
    () =>
      helpTopics().filter(
        // Un filtre de public laisse passer les fiches communes, écrites pour les deux publics.
        (item) => (role === "tout" || item.role === role || item.role === "commun") && matches(item, query),
      ),
    [query, role],
  );
  // Recherche précise (trois fiches au plus) : fiches dépliées d'emblée, repliables ensuite.
  useEffect(() => {
    if (tab !== "all" || query.trim().length < 3 || results.length > 3) return;
    setExpanded((prev) => new Set([...prev, ...results.map((item) => item.id)]));
  }, [tab, query, results]);
  const glossary = useMemo(() => {
    const needle = fold(query.trim());
    return glossaryEntries().filter((e) => !needle || fold(e.term).includes(needle) || fold(e.definition).includes(needle));
  }, [query]);

  return (
    <div className={clsx("fixed inset-0 top-(--gx-strip-h) z-[70] flex justify-end bg-ink/20", motion("fade", closing))} role="presentation">
      <aside
        ref={panel}
        role="dialog"
        aria-label={t("Aide")}
        className={clsx("flex h-full w-full max-w-[540px] flex-col border-l border-line bg-surface shadow-float", motion("right", closing))}
      >
        <header className="flex min-h-10 shrink-0 items-center gap-2.5 border-b border-line py-1.5 pr-2 pl-4">
          <CircleHelp size={16} className="shrink-0 text-ink" aria-hidden />
          <div className="min-w-0 flex-1 leading-snug">
            <h2 className="text-ui font-semibold text-ink">{t("Aide")}</h2>
            <p className="truncate text-meta text-ink-muted">{screen ? screenLabel(screen) : t("Generex Web")}</p>
          </div>
          <IconButton size="sm" onClick={close} title={t("Fermer (Échap)")} aria-label={t("Fermer")}>
            <X size={16} />
          </IconButton>
        </header>

        <div className="shrink-0 space-y-3 border-b border-line px-4 py-3">
          <Segmented<HelpTab>
            role="tab"
            ariaLabel={t("Rubriques de l'aide")}
            value={tab}
            onChange={setTab}
            options={[
              { value: "screen", label: t("Cet écran"), icon: CircleHelp, disabled: !screen },
              { value: "all", label: t("Toute l'aide"), icon: Search },
              { value: "glossary", label: t("Glossaire"), icon: BookA },
            ]}
          />
          {tab !== "screen" && (
            <div className="flex items-center gap-2">
              <SearchField
                className="min-w-0 flex-1"
                inputRef={search}
                value={query}
                onChange={setQuery}
                placeholder={tab === "glossary" ? t("Rechercher un sigle…") : t("Rechercher dans l'aide…")}
              />
              {tab === "all" && (
                <Segmented<RoleFilter>
                  size="sm"
                  fill={false}
                  ariaLabel={t("Public")}
                  value={role}
                  onChange={setRole}
                  className="shrink-0"
                  options={[
                    { value: "tout", label: t("Tout") },
                    { value: "joueur", label: t(ROLE_LABELS.joueur) },
                    { value: "direx", label: t(ROLE_LABELS.direx) },
                  ]}
                />
              )}
            </div>
          )}
        </div>

        <div ref={body} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {tab === "screen" &&
            (screenTopics.length ? (
              <div className="space-y-2">
                {screenTopics.map((item) => (
                  <TopicCard key={item.id} topic={item} open={expanded.has(item.id)} onToggle={() => toggle(item.id)} onRelated={goTo} />
                ))}
              </div>
            ) : (
              <EmptyState variant="inline" className="-mx-4 -my-4">
                {t("Cet écran arrive dans une prochaine version : son aide viendra avec lui.")}
              </EmptyState>
            ))}
          {tab === "all" && !query.trim() && <GuideHelpCard className="mb-5" />}
          {tab === "all" &&
            (results.length ? (
              <div className="space-y-6">
                <p className="text-meta text-ink-muted">
                  {query.trim()
                    ? t("{fiches} pour « {query} »", { fiches: plural(results.length, t("fiche"), t("fiches")), query: query.trim() })
                    : plural(results.length, t("fiche"), t("fiches"))}
                </p>
                {SCREENS.map((s) => {
                  const items = results.filter((item) => item.screen === s.key);
                  if (!items.length) return null;
                  return (
                    <section key={s.key} className="space-y-2">
                      <SectionLabel as="h3" inset>
                        {screenLabel(s.key)}
                      </SectionLabel>
                      {items.map((item) => (
                        <TopicCard
                          key={item.id}
                          topic={item}
                          open={expanded.has(item.id)}
                          onToggle={() => toggle(item.id)}
                          onRelated={goTo}
                          highlight={query}
                        />
                      ))}
                    </section>
                  );
                })}
              </div>
            ) : (
              <EmptyState variant="inline" className="-mx-4 -my-4">
                {t("Aucune fiche ne correspond à « {query} ». Essayez un autre mot, ou le glossaire.", { query: query.trim() })}
              </EmptyState>
            ))}
          {tab === "glossary" &&
            (glossary.length ? (
              <dl className="divide-y divide-line">
                {glossary.map((e) => {
                  const anchor = glossaryAnchor(e.term);
                  return (
                    // Le sigle surligné l'est dans un bloc décollé des filets (rien ne touche une ligne grise, R4).
                    <div key={e.term} id={`aide-${anchor}`} className="scroll-my-3 py-1">
                      <div className={clsx("grid grid-cols-[92px_1fr] gap-3 rounded-md px-1 py-1.5 transition-colors", topic === anchor && "bg-selected")}>
                        <dt className="text-ui font-semibold break-words text-ink">{e.term}</dt>
                        <dd className="text-ui leading-relaxed text-ink-soft">{e.definition}</dd>
                      </div>
                    </div>
                  );
                })}
              </dl>
            ) : (
              <EmptyState variant="inline" className="-mx-4 -my-4">
                {t("Aucun sigle ne correspond à « {query} ».", { query: query.trim() })}
              </EmptyState>
            ))}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-line px-4 py-2.5 text-meta text-ink-muted">
          <span className="flex items-center gap-1">
            <Button variant="link" onClick={openOnboarding}>
              <Compass size={14} /> {t("Prise en main")}
            </Button>
            {/* Retours (registre F39) : l'aide ferme, le bouton « Retours » pulse et son menu s'ouvre. */}
            <Button variant="link" onClick={askFeedback} title={t("Suggérer une évolution ou signaler une anomalie")}>
              <MessageSquarePlus size={14} /> {t("Une idée, une anomalie ?")}
            </Button>
          </span>
          <span className="flex items-center gap-1.5">{withKey(t("{touche} ouvre l'aide de la vue affichée"))}</span>
        </footer>
      </aside>
    </div>
  );
}

function TopicCard({
  topic,
  open,
  onToggle,
  onRelated,
  highlight,
}: {
  topic: HelpTopic;
  open: boolean;
  onToggle: () => void;
  onRelated: (id: string) => void;
  highlight?: string;
}) {
  const related = (topic.related ?? []).map(topicById).filter((item): item is HelpTopic => Boolean(item));
  const bodyId = useId();
  return (
    <article
      id={`aide-${topic.id}`}
      className={clsx(
        "scroll-mt-3 overflow-hidden rounded-xl border bg-surface transition-colors",
        open ? "border-line-strong" : "border-line hover:border-line-strong",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="focus-inset flex min-h-10 w-full items-start gap-2.5 px-3.5 py-2.5 text-left"
      >
        <Badge tone={ROLE_TONES[topic.role]} className="mt-px">
          {t(ROLE_LABELS[topic.role])}
        </Badge>
        <span className="min-w-0 flex-1 pt-0.5 text-title leading-snug font-semibold text-ink">
          <Highlight text={topic.title} query={highlight} />
        </span>
        <DisclosureChevron open={open} className="mt-1" />
      </button>
      <Disclosure open={open} id={bodyId}>
        <div className="space-y-3 px-3.5 pb-3.5">
          <Body text={topic.body} highlight={highlight} />
          {topic.id === "ordre.centac" && <RotationFlowFigure />}
          {related.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-line pt-2.5">
              <span className="text-meta font-semibold text-ink-muted">{t("Voir aussi")}</span>
              {related.map((r) => (
                <Button key={r.id} variant="link" size="sm" onClick={() => onRelated(r.id)}>
                  <CornerDownRight size={14} /> {r.title}
                </Button>
              ))}
            </div>
          )}
        </div>
      </Disclosure>
    </article>
  );
}

/** Texte d'une fiche (lecture longue, 13 px) : paragraphes, puces « - », touches entre accents graves. */
function Body({ text, highlight }: { text: string; highlight?: string }) {
  const blocks = text.split(/\n{2,}/);
  return (
    <div className="space-y-2.5 text-title leading-relaxed text-ink-soft">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const first = lines[0] ?? "";
        const bullets = lines.filter((l) => l.startsWith("- "));
        if (bullets.length && bullets.length === lines.length - (first.startsWith("- ") ? 0 : 1)) {
          const lead = first.startsWith("- ") ? null : first;
          return (
            <div key={i} className="space-y-1.5">
              {lead && (
                <p>
                  <Inline text={lead} highlight={highlight} />
                </p>
              )}
              <ul className="space-y-1 pl-4">
                {bullets.map((b, j) => (
                  <li key={j} className="list-disc marker:text-ink-muted">
                    <Inline text={b.slice(2)} highlight={highlight} />
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return (
          <p key={i}>
            <Inline text={block} highlight={highlight} />
          </p>
        );
      })}
    </div>
  );
}

function Inline({ text, highlight }: { text: string; highlight?: string }) {
  const parts = text.split(/`([^`]+)`/);
  return (
    <>
      {parts.map((part, i) => (i % 2 === 1 ? <Kbd key={i}>{part}</Kbd> : <Highlight key={i} text={part} query={highlight} />))}
    </>
  );
}

/** Termes recherchés surlignés (sans tenir compte des accents ni de la casse). */
function Highlight({ text, query }: { text: string; query?: string }) {
  const needle = fold((query ?? "").trim());
  if (needle.length < 2) return <>{text}</>;
  const folded = fold(text);
  const out: ReactNode[] = [];
  let from = 0;
  let at = folded.indexOf(needle);
  while (at >= 0) {
    out.push(text.slice(from, at));
    out.push(
      <mark key={at} className="rounded-sm bg-accent-soft px-px text-ink">
        {text.slice(at, at + needle.length)}
      </mark>,
    );
    from = at + needle.length;
    at = folded.indexOf(needle, from);
  }
  out.push(text.slice(from));
  return <>{out}</>;
}

/** Phrase traduite dont `{touche}` reçoit la touche « ? », à sa place dans la langue affichée. */
function withKey(text: string): ReactNode[] {
  return text.split("{touche}").flatMap((part, i) => (i ? [<Kbd key={i}>?</Kbd>, part] : [part]));
}
